import { post as apiPost, put as apiPut, getById as apiGetById } from './apiService';
import { getActiveTeam } from './profileService';
import type { MysteryShopProperty, MysteryShopAgent, MysteryShopCall, SearchParams, CallFilter } from '../types/mysteryShop';

let useMock = false;
export function setMysteryShopMock(value: boolean) { useMock = value; }

// ============================================================================
// MOCK DATA GENERATION
// ============================================================================

const propertyNames = [
  "Pointe Grande Apartments", "The Meridian", "Cascade Heights", "Willow Creek Commons",
  "Harbor View Residences", "Summit Place", "The Ashford", "Parkside Terrace",
  "Riverside Landing", "The Wellington", "Brookstone Manor", "Copper Ridge",
  "The Ellison", "Maple Grove", "Crescent Bay", "The Hartwell",
  "Stonebridge Crossing", "The Avalon", "Foxwood Gardens", "The Lancaster",
  "Sierra Vista", "The Bennington", "Oakwood Flats", "The Chamberlain",
  "Pinecrest Village", "The Davenport", "Lakeside Retreat", "The Emerson",
  "Cedar Park Place", "The Fitzgerald", "Mountainview Terrace", "The Greenwich",
  "Aspen Ridge", "The Hamilton", "Birchwood Commons", "The Irving",
  "Chestnut Hill", "The Jefferson", "Dogwood Landing", "The Kingston"
];

const agentFirstNames = ["Sarah", "Michael", "Jessica", "David", "Emily", "James", "Ashley", "Robert", "Amanda", "Christopher", "Brittany", "Matthew", "Stephanie", "Daniel", "Nicole", "Andrew", "Rachel", "Joshua", "Megan", "Ryan"];
const agentLastNames = ["Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis", "Rodriguez", "Martinez", "Anderson", "Taylor", "Thomas", "Moore", "Jackson", "Martin", "Lee", "Thompson", "White", "Harris", "Clark"];

const discoveryCriteria = [
  "Asked floorplan needs",
  "Full name collected",
  "Phone number collected",
  "Collected email address",
  "Website interest asked",
  "Move-in timeframe asked",
  "Occupants count asked",
  "Pet ownership asked"
];

const needsMatchingCriteria = [
  "Asked about must-haves/priorities",
  "Mentioned specific amenities",
  "Connected features to stated needs"
];

const rapportCriteria = [
  "Professional greeting with property name",
  "Used prospect's name multiple times",
  "Acknowledged/repeated back needs",
  "Asked if prospect had questions",
  "Friendly closing with thank you"
];

const followUpCriteria = [
  "Confirmed specific follow-up action",
  "Verified contact method works"
];

function makeBaseFields(id: string) {
  const now = new Date().toISOString();
  return {
    id,
    createdAt: now,
    updatedAt: now,
    deletedAt: null as null,
    instanceVersion: 1,
    createdBy: 'mock',
    updatedBy: 'mock',
    metadata: {} as Record<string, unknown>
  };
}

// Fixed set of mock agents (deterministic, no random names)
const mockAgents: MysteryShopAgent[] = Array.from({ length: 20 }, (_, i) => ({
  ...makeBaseFields(`agent-${i}`),
  name: `${agentFirstNames[i]} ${agentLastNames[i]}`
}));

// Fixed set of mock properties (limited to 9 for display)
const mockProperties: MysteryShopProperty[] = propertyNames.slice(0, 9).map((name, index) => ({
  ...makeBaseFields(`prop-${index}`),
  name,
  address: `${1000 + index * 47} ${['Main', 'Oak', 'Elm', 'Park', 'Lake'][index % 5]} Street`
}));

// Map of propertyId → agentId[] (deterministic assignment)
const propertyAgentMap = new Map<string, string[]>(
  mockProperties.map((prop, index) => {
    const agentCount = (index % 3) + 1;
    const agentIds = Array.from({ length: agentCount }, (_, j) => `agent-${(index + j) % mockAgents.length}`);
    return [prop.id, agentIds];
  })
);

function generateCriteriaResults(targetScore: number, maxScore: number, criteriaList: string[]): { text: string; result: string; passed: boolean }[] {
  const results: { text: string; result: string; passed: boolean }[] = [];
  let remaining = targetScore;
  const pointsPerItem = maxScore / criteriaList.length;

  criteriaList.forEach((criteria, index) => {
    const isLast = index === criteriaList.length - 1;
    let passed: boolean;
    if (isLast) {
      passed = remaining > 0;
    } else {
      const probability = remaining / (maxScore - (index * pointsPerItem));
      passed = Math.random() < Math.min(probability, 0.85);
      if (passed) remaining -= pointsPerItem;
    }
    results.push({ text: criteria, result: passed ? 'Yes' : 'No', passed });
  });

  return results;
}

function generateTranscript(propertyName: string, agentName: string | null): string {
  const agent = agentName || "Leasing Agent";
  return `[Call Recording Transcript]

${agent}: Thank you for calling ${propertyName}, this is ${agent.split(' ')[0]}, how can I help you today?

Prospect: Hi, I'm looking for a one-bedroom apartment, hopefully available next month.

${agent}: Great! I'd be happy to help you find the perfect home. We do have some one-bedrooms available. Can I get your name?

Prospect: Sure, it's Alex.

${agent}: Nice to meet you, Alex. Let me tell you about what we have available...

[Transcript continues...]`;
}

let mockCallCounter = 1;

function generateCall(property: MysteryShopProperty, agentIds: string[], date: Date): MysteryShopCall {
  const tourInvited = Math.random() > 0.25;
  const tourScore = tourInvited ? 50 : 0;

  const discoveryScore = Math.round((Math.random() * 0.6 + 0.2) * 20);
  const needsScore = Math.round((Math.random() * 0.5 + 0.4) * 15);
  const rapportScore = Math.round((Math.random() * 0.5 + 0.3) * 10);
  const followUpScore = Math.round((Math.random() * 0.6 + 0.3) * 5);
  const overallScore = tourScore + discoveryScore + needsScore + rapportScore + followUpScore;

  const hasAgent = Math.random() > 0.15;
  const agentId = hasAgent ? agentIds[Math.floor(Math.random() * agentIds.length)] : null;
  const agentName = agentId ? (mockAgents.find(a => a.id === agentId)?.name ?? null) : null;

  return {
    ...makeBaseFields(`00000000-0000-0000-0000-${String(++mockCallCounter).padStart(12, '0')}`),
    date: date.toISOString(),
    propertyId: property.id,
    propertyName: property.name,
    agentId,
    agentName,
    overallScore,
    tourInvited,
    scores: [
      { order: 1, title: 'Tour Invitation', highlighted: true, score: tourScore, max: 50, weight: 50 },
      {
        order: 2, title: 'Discovery & Qualification', highlighted: false,
        score: discoveryScore, max: 20, weight: 20,
        criteria: generateCriteriaResults(discoveryScore, 20, discoveryCriteria)
      },
      {
        order: 3, title: 'Needs Matching', highlighted: false,
        score: needsScore, max: 15, weight: 15,
        criteria: generateCriteriaResults(needsScore, 15, needsMatchingCriteria)
      },
      {
        order: 4, title: 'Rapport & Professionalism', highlighted: false,
        score: rapportScore, max: 10, weight: 10,
        criteria: generateCriteriaResults(rapportScore, 10, rapportCriteria)
      },
      {
        order: 5, title: 'Follow-up', highlighted: false,
        score: followUpScore, max: 5, weight: 5,
        criteria: generateCriteriaResults(followUpScore, 5, followUpCriteria)
      }
    ],
    recordingUrl: 'https://dxc03zgurdly9.cloudfront.net/53936861054330fdf01b6c793259f28cdb1aa3a1182d798e511957d0b2c1aebc/recording.wav',
    transcript: generateTranscript(property.name, agentName)
  };
}

function generateAllMockCalls(): MysteryShopCall[] {
  const calls: MysteryShopCall[] = [];
  const now = new Date();
  const curMonth = now.getMonth();
  const curYear = now.getFullYear();
  const prevMonth = curMonth === 0 ? 11 : curMonth - 1;
  const prevYear = curMonth === 0 ? curYear - 1 : curYear;

  mockProperties.forEach(property => {
    const agentIds = propertyAgentMap.get(property.id) ?? [];

    const curCount = Math.floor(Math.random() * 2) + 1;
    for (let i = 0; i < curCount; i++) {
      const day = Math.floor(Math.random() * Math.min(now.getDate(), 28)) + 1;
      calls.push(generateCall(property, agentIds,
        new Date(curYear, curMonth, day, Math.floor(Math.random() * 10) + 9, Math.floor(Math.random() * 60))));
    }

    const prevCount = Math.floor(Math.random() * 2) + 1;
    for (let i = 0; i < prevCount; i++) {
      const day = Math.floor(Math.random() * 28) + 1;
      calls.push(generateCall(property, agentIds,
        new Date(prevYear, prevMonth, day, Math.floor(Math.random() * 10) + 9, Math.floor(Math.random() * 60))));
    }
  });

  return calls;
}

// All mock calls include criteria (getCalls strips them; getCallById keeps them)
// Lazy: generated on first use so toggling useMock at runtime works
let allMockCalls: MysteryShopCall[] = [];
function getMockCalls(): MysteryShopCall[] {
  if (allMockCalls.length === 0) allMockCalls = generateAllMockCalls();
  return allMockCalls;
}

function stripCriteria(call: MysteryShopCall): MysteryShopCall {
  return {
    ...call,
    scores: call.scores.map(s => ({ ...s, criteria: undefined }))
  };
}

function computePropertyStats(propertyId: string, calls: MysteryShopCall[]) {
  const now = new Date();
  const curMonth = now.getMonth();
  const curYear = now.getFullYear();
  const prevMonth = curMonth === 0 ? 11 : curMonth - 1;
  const prevYear = curMonth === 0 ? curYear - 1 : curYear;

  const propCalls = calls.filter(c => c.propertyId === propertyId);
  const curCalls = propCalls.filter(c => {
    const d = new Date(c.date);
    return d.getMonth() === curMonth && d.getFullYear() === curYear;
  });
  const prevCalls = propCalls.filter(c => {
    const d = new Date(c.date);
    return d.getMonth() === prevMonth && d.getFullYear() === prevYear;
  });

  const avgScore = curCalls.length > 0
    ? curCalls.reduce((s, c) => s + c.overallScore, 0) / curCalls.length
    : null;
  const prevAvg = prevCalls.length > 0
    ? prevCalls.reduce((s, c) => s + c.overallScore, 0) / prevCalls.length
    : null;
  const tourRate = curCalls.length > 0
    ? (curCalls.filter(c => c.tourInvited).length / curCalls.length) * 100
    : null;

  return {
    callCount: propCalls.length,
    avgScore,
    tourRate,
    trend: avgScore !== null && prevAvg !== null ? avgScore - prevAvg : null
  };
}

function computeAgentStats(agentId: string, calls: MysteryShopCall[]) {
  const agentCalls = calls.filter(c => c.agentId === agentId);
  const avgScore = agentCalls.length > 0
    ? agentCalls.reduce((s, c) => s + c.overallScore, 0) / agentCalls.length
    : null;
  const tourRate = agentCalls.length > 0
    ? (agentCalls.filter(c => c.tourInvited).length / agentCalls.length) * 100
    : null;
  const propertyCount = new Set(agentCalls.map(c => c.propertyId)).size;

  return { callCount: agentCalls.length, avgScore, tourRate, trend: null, propertyCount };
}

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

// POST /property/search?page=1&pageSize=100&orderBy=updated&order=desc  (body = {})
export async function getProperties(params?: SearchParams): Promise<MysteryShopProperty[]> {
  if (!useMock) {
    const team = getActiveTeam();
    return apiPost<MysteryShopProperty[]>('/property/search?page=1&pageSize=100&orderBy=updated&order=desc', { clientId: team?.clientId });
  }
  return mockProperties.map(prop => ({
    ...prop,
    performance: computePropertyStats(prop.id, getMockCalls())
  }));
}

// POST /agent/search?page=1&pageSize=100&orderBy=updated&order=desc  (body = {})
export async function getAgents(params?: SearchParams): Promise<MysteryShopAgent[]> {
  if (!useMock) {
    const team = getActiveTeam();
    return apiPost<MysteryShopAgent[]>('/agent/search?page=1&pageSize=100&orderBy=updated&order=desc', { clientId: team?.clientId });
  }
  return mockAgents
    .map(agent => {
      const { propertyCount, ...perfStats } = computeAgentStats(agent.id, getMockCalls());
      return { ...agent, propertyCount, performance: perfStats };
    })
    .filter(a => (a.performance?.callCount ?? 0) > 0)
    .sort((a, b) => (b.performance?.avgScore ?? 0) - (a.performance?.avgScore ?? 0));
}

// POST /retell-call/search?expand=...&startDate=...&endDate=...&page=...  (body = { propertyId?, agentId? })
// All query params: expand, startDate, endDate, page, pageSize, orderBy, order
export async function getCalls(
  filter?: CallFilter,
  params?: SearchParams,
  expand?: string
): Promise<MysteryShopCall[]> {
  if (!useMock) {
    const qs = new URLSearchParams();
    if (expand) qs.set('expand', expand);
    if (params?.startDate) qs.set('startDate', params.startDate);
    if (params?.endDate) qs.set('endDate', params.endDate);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params?.orderBy) qs.set('orderBy', params.orderBy);
    if (params?.order) qs.set('order', params.order);
    const team = getActiveTeam();
    return apiPost<MysteryShopCall[]>(`/retell-call/search?${qs}`, { ...filter, clientId: team?.clientId, published: true });
  }
  let filtered = getMockCalls();
  if (filter?.propertyId) filtered = filtered.filter(c => c.propertyId === filter.propertyId);
  if (filter?.agentId)    filtered = filtered.filter(c => c.agentId === filter.agentId);
  if (params?.startDate)  filtered = filtered.filter(c => c.date >= params.startDate!);
  if (params?.endDate)    filtered = filtered.filter(c => c.date <= params.endDate!);
  if (expand?.includes('property')) {
    filtered = filtered.map(c => ({
      ...c,
      property: mockProperties.find(p => p.id === c.propertyId)
    }));
  }
  if (expand?.includes('agent')) {
    filtered = filtered.map(c => ({
      ...c,
      agent: c.agentId ? mockAgents.find(a => a.id === c.agentId) : undefined
    }));
  }
  return filtered.map(stripCriteria);
}

// GET /retell-call/{id}  — returns full detail including CriteriaResult[] on each ScoreDetail
export async function getCallById(id: string): Promise<MysteryShopCall> {
  if (!useMock) {
    return apiGetById<MysteryShopCall>('/retell-call', id);
  }
  const call = getMockCalls().find(c => c.id === id);
  if (!call) throw new Error(`Call ${id} not found`);
  return call;
}

// GET /property/{id}?expand=agents
export async function getPropertyById(id: string, expand?: string): Promise<MysteryShopProperty> {
  if (!useMock) {
    return apiGetById<MysteryShopProperty>('/property', id, expand ? `expand=${expand}` : undefined);
  }
  const prop = mockProperties.find(p => p.id === id);
  if (!prop) throw new Error(`Property ${id} not found`);
  return { ...prop, performance: computePropertyStats(id, getMockCalls()) };
}

// GET /agent/{id}?expand=properties
export async function getAgentById(id: string, expand?: string): Promise<MysteryShopAgent> {
  if (!useMock) {
    return apiGetById<MysteryShopAgent>('/agent', id, expand ? `expand=${expand}` : undefined);
  }
  const agent = mockAgents.find(a => a.id === id);
  if (!agent) throw new Error(`Agent ${id} not found`);
  const { propertyCount, ...perfStats } = computeAgentStats(id, getMockCalls());
  return { ...agent, propertyCount, performance: perfStats };
}

// PUT /agent/{id}  — full model replace
export async function updateAgent(agent: MysteryShopAgent): Promise<MysteryShopAgent> {
  return apiPut<MysteryShopAgent>(`/agent/${agent.id}`, agent);
}

export const mysteryShopService = {
  getProperties,
  getAgents,
  getCalls,
  getCallById,
  getPropertyById,
  getAgentById,
  updateAgent
};
